import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Sparkles, FileText, StickyNote, Clock, Download, CheckCircle2, Trash2, Mail, Info } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { StatusChip, SecureImage, Modal, fmtDate, displayName } from "@/components/common";
import ContactFields from "@/components/ContactFields";
import DuplicatePanel from "@/components/DuplicatePanel";
import NotesPanel from "@/components/NotesPanel";
import RepliesPanel from "@/components/RepliesPanel";
import InterestSuggestion from "@/components/InterestSuggestion";

function WhatNext({ c, onClose }) {
  const nav = useNavigate();
  return (
    <Modal open onClose={onClose} title="Contact saved. What next?" testid="what-next-dialog">
      <div className="grid gap-2">
        <button data-testid="whatnext-ai-btn" onClick={() => nav(`/compose?contact=${c.id}&mode=ai`)} className="cd-btn-primary justify-start"><Sparkles className="h-5 w-5" />Generate AI email</button>
        <button data-testid="whatnext-template-btn" onClick={() => nav(`/compose?contact=${c.id}&mode=template`)} className="cd-btn-secondary justify-start"><FileText className="h-5 w-5" />Use saved template</button>
        <button data-testid="whatnext-note-btn" onClick={() => { onClose(); setTimeout(() => document.getElementById("notes")?.scrollIntoView({ behavior: "smooth" }), 50); }} className="cd-btn-secondary justify-start"><StickyNote className="h-5 w-5" />Add note</button>
        <button data-testid="whatnext-later-btn" onClick={onClose} className="cd-btn-ghost justify-start"><Clock className="h-5 w-5" />Later</button>
      </div>
    </Modal>
  );
}

function DeleteDialog({ c, onClose }) {
  const nav = useNavigate();
  const [impact, setImpact] = useState(null);
  useEffect(() => { api.get(`/contacts/${c.id}/deletion-impact`).then((r) => setImpact(r.data)); }, [c.id]);
  const del = async () => { await api.delete(`/contacts/${c.id}`); toast.success("Contact deleted"); nav("/contacts"); };
  return (
    <Modal open onClose={onClose} title="Delete contact?" testid="delete-dialog">
      {impact && (
        <div className="space-y-3 text-sm">
          <p>This permanently removes <b>{displayName(c)}</b> and:</p>
          <ul className="list-disc pl-5 text-slate-600">
            <li>{impact.notes} note(s) and {impact.activities} timeline entries</li>
            <li>{impact.files} file(s) (card images, voice notes, attachments) — access is revoked immediately</li>
            <li>Unsent drafts; queued emails are cancelled. {impact.messages} email record(s) in total</li>
          </ul>
          <p className="text-slate-500">Already-sent emails cannot be recalled. The address stays on your suppression list if it was added.</p>
          <button data-testid="delete-confirm-btn" onClick={del} className="cd-btn-danger w-full">Delete permanently</button>
        </div>
      )}
    </Modal>
  );
}

export default function ContactDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [c, setC] = useState(null);
  const [events, setEvents] = useState([]);
  const [senders, setSenders] = useState([]);
  const [acts, setActs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [whatNext, setWhatNext] = useState(params.get("saved") === "1");
  const [deleting, setDeleting] = useState(false);
  const [tab, setTab] = useState(params.get("tab") || "details");

  const load = () => {
    api.get(`/contacts/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/contacts/${id}/activities`).then((r) => setActs(r.data));
    api.get(`/messages?contact_id=${id}`).then((r) => setMsgs(r.data));
  };
  useEffect(() => {
    load();
    api.get("/events").then((r) => setEvents(r.data));
    api.get("/senders").then((r) => setSenders(r.data));
  }, [id]); // eslint-disable-line

  const save = async (data) => {
    if (!Object.keys(data).length) return toast.message("No changes");
    setSaving(true);
    try { await api.patch(`/contacts/${id}`, { data }); toast.success("Saved"); load(); } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };
  const confirm = async () => {
    try { await api.post(`/contacts/${id}/confirm-review`); load(); setParams({}); setWhatNext(true); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!c) return <p className="text-slate-500">Loading…</p>;
  const images = (c.files || []).filter((f) => f.kind === "card_image");
  const isReview = c.status === "needs_review" || params.get("review") === "1";

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between cd-fade">
        <div>
          <h1 data-testid="contact-name" className="text-2xl md:text-3xl font-bold">{displayName(c)}</h1>
          <p className="text-slate-500">{[c.job_title, c.company].filter(Boolean).join(" · ")}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2"><StatusChip status={c.status} testid="contact-status" /><span className="text-xs text-slate-400">Source: {c.source_type?.replace(/_/g, " ")} · {fmtDate(c.capture_date)}</span></div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/compose?contact=${c.id}`} data-testid="contact-compose-btn" className="cd-btn-primary"><Mail className="h-4 w-4" />Write email</Link>
          {c.status !== "replied" && <button data-testid="contact-mark-replied-btn" onClick={() => save({ status: "replied" })} className="cd-btn-secondary">Mark replied</button>}
          <button data-testid="contact-delete-btn" onClick={() => setDeleting(true)} className="cd-btn-ghost text-rose-600"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>

      {isReview && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="review-banner">
          <p className="font-bold text-amber-900">Review extracted details</p>
          <p className="mt-1 text-sm text-amber-800">{(c.review_reasons || []).length ? c.review_reasons.join(" · ") : "Check the details against the original, then confirm."}</p>
          {c.review_note && <p className="mt-1 text-sm text-amber-800">{c.review_note}</p>}
          <p className="mt-1 text-xs text-amber-700">Highlighted fields are uncertain. Extracted facts come from the source; purple “AI suggestion” labels are guesses you can edit.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button data-testid="confirm-review-btn" onClick={confirm} className="cd-btn-primary"><CheckCircle2 className="h-4 w-4" />Details are correct — save</button>
            {c.multi_card_warning && c.captures?.[0]?.extra_cards?.length > 0 && (
              <button data-testid="split-cards-btn" onClick={async () => { try { const r = await api.post(`/captures/${c.captures[0].id}/split`); toast.success(`${r.data.contact_ids.length} more contact(s) created`); load(); } catch (e) { toast.error(errMsg(e)); } }} className="cd-btn-secondary">Create separate contacts for other cards</button>
            )}
          </div>
        </div>
      )}
      <DuplicatePanel contact={c} reload={load} />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="space-y-4 lg:sticky lg:top-20 lg:self-start">
          <div className="cd-card">
            <p className="cd-label">Original source</p>
            {images.length ? images.map((f) => (
              <div key={f.id} className="mb-3">
                <SecureImage fileId={f.id} testid="source-image" className="max-h-[420px] w-full rounded-xl bg-slate-50 object-contain" />
                <a href={fileUrl(f.id, true)} data-testid={`download-image-${f.id}`} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-teal-700"><Download className="h-3.5 w-3.5" />Download original ({f.side || "image"})</a>
              </div>
            )) : <p className="text-sm text-slate-500">No image for this contact.</p>}
            {c.source_urls?.length > 0 && <p className="mt-2 break-all text-xs text-slate-500" data-testid="source-urls">Imported from: {c.source_urls.join(", ")}</p>}
            {c.original_text && <details className="mt-2"><summary className="cursor-pointer text-sm font-semibold">Original text</summary><pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-xl bg-slate-50 p-3 text-xs">{c.original_text}</pre></details>}
            {c.english_summary && <p className="mt-3 rounded-xl bg-violet-50/50 p-3 text-sm"><Info className="mr-1 inline h-4 w-4 text-violet-600" /><b>English summary (AI):</b> {c.english_summary}</p>}
          </div>
        </div>
        <div className="cd-card">
          <div className="mb-4 flex gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1">
            {[["details", "Details"], ["replies", "Replies"], ["notes", "Notes & files"], ["emails", `Emails (${msgs.length})`], ["timeline", "Timeline"]].map(([k, l]) => (
              <button key={k} data-testid={`contact-tab-${k}`} onClick={() => setTab(k)} className={`h-10 flex-1 whitespace-nowrap rounded-lg px-3 text-sm font-semibold ${tab === k ? "bg-white text-teal-700 shadow-sm" : "text-slate-600"}`}>{l}</button>
            ))}
          </div>
          {tab === "details" && <InterestSuggestion contact={c} reload={load} />}
          {tab === "details" && <ContactFields contact={c} events={events} senders={senders} onSave={save} saving={saving} />}
          {tab === "replies" && <RepliesPanel contactId={c.id} />}
          {tab === "notes" && <div id="notes"><NotesPanel contactId={c.id} files={c.files} reload={load} /></div>}
          {tab === "emails" && (
            <div className="space-y-2" data-testid="contact-emails">
              {msgs.length === 0 && <p className="text-sm text-slate-500">No emails yet.</p>}
              {msgs.map((m) => (
                <Link key={m.id} to={`/compose?message=${m.id}`} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 p-3 hover:border-teal-500">
                  <div className="min-w-0"><p className="truncate font-semibold">{m.subject || "(generating…)"}</p><p className="text-xs text-slate-500">{fmtDate(m.sent_at || m.created_at)}</p></div>
                  <StatusChip status={m.status} />
                </Link>
              ))}
            </div>
          )}
          {tab === "timeline" && (
            <ol className="space-y-3 border-l-2 border-slate-100 pl-4" data-testid="contact-timeline">
              {acts.map((a) => (
                <li key={a.id} className="relative"><span className="absolute -left-[23px] top-1.5 h-3 w-3 rounded-full border-2 border-white bg-teal-500" />
                  <p className="text-sm font-medium">{a.text}</p><p className="text-xs text-slate-400">{fmtDate(a.created_at)}</p></li>
              ))}
            </ol>
          )}
        </div>
      </div>
      {whatNext && <WhatNext c={c} onClose={() => { setWhatNext(false); setParams({}); }} />}
      {deleting && <DeleteDialog c={c} onClose={() => setDeleting(false)} />}
    </div>
  );
}
